import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart2, RefreshCw, TrendingUp, TrendingDown, Euro,
  Plus, Trash2, Check, X, Pencil, ChevronLeft, ChevronRight, ChevronDown, Lock,
} from 'lucide-react';
import apiClient from '../../api/apiClient';
import { AccountingData, AccountingPeriod, OpexItem, ShowNotificationFn } from './crm.types';
import { CrmEmailSyncBanner } from './CrmEmailSyncBanner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return (n < 0 ? '−' : '') + Math.abs(n).toFixed(2) + ' €';
}

function fmtPct(n: number): string {
  return (n >= 0 ? '+' : '') + n.toFixed(1) + ' %';
}

function numCls(n: number, bold = false): string {
  const b = bold ? 'font-bold' : '';
  if (n > 0) return `${b} text-emerald-600`.trim();
  if (n < 0) return `${b} text-red-500`.trim();
  return `${b} text-gray-400`.trim();
}

// Facteur de mise à l'échelle du montant mensuel pour la vue donnée
function opexFactor(view: string): number {
  if (view === 'annual') return 12;
  if (view === 'weekly') return 12 / 52;
  return 1;
}

// OPEX pour une période précise (récurrents + ponctuels correspondants)
function opexForPeriod(items: OpexItem[], periodKey: string, factor: number): number {
  return items.reduce((s, o) => {
    if (!o.period_type || o.period_type === 'recurring') {
      return s + o.monthly_amount * factor;
    }
    return s + (o.period_key === periodKey ? o.monthly_amount : 0);
  }, 0);
}

/** Max de l’axe Y : légèrement au-dessus du pic, arrondi « lisible » (grille et barres alignées). */
function chartScaleMax(rawMax: number): number {
  if (!Number.isFinite(rawMax) || rawMax <= 0) return 100;
  const target = rawMax * 1.18;
  const exp = Math.floor(Math.log10(target));
  const pow = Math.pow(10, exp);
  const n = target / pow;
  const steps = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10];
  const step = steps.find(s => s >= n) ?? 10;
  return step * pow;
}

// ── SVG Chart ─────────────────────────────────────────────────────────────────

interface ChartProps {
  periods: AccountingPeriod[];
  view: string;
}

const W = 900;
const H = 270;   // hauteur SVG augmentée pour les étiquettes au-dessus de petites barres
const PL = 54;
const PR = 18;
const PT = 32;   // espace pour les étiquettes au-dessus des barres
const PB = 34;
const CW = W - PL - PR;
const CH = H - PT - PB;

const MAX_PERIODS = 12;

const AccountingChart: React.FC<ChartProps> = ({ periods, view }) => {
  const [hovered, setHovered] = useState<number | null>(null);

  // Filtrer les périodes vides puis limiter à 12 les plus récentes
  const filtered = periods.filter(p => p.count > 0 || p.ca > 0);
  const data = filtered.slice(-MAX_PERIODS);

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-44 text-sm text-gray-400">
        Aucune vente expédiée ou finalisée sur cette période
      </div>
    );
  }

  const n = data.length;

  const rawMax = Math.max(...data.map(p => Math.max(p.ca, Math.abs(p.net))), 1);
  const scaleMax = chartScaleMax(rawMax);

  const gW = CW / n;
  // Barres plus larges quand peu de périodes (1…12), avec plafond pour ne pas fusionner au-delà
  const barFrac = n <= 2 ? 0.36 : n <= 3 ? 0.32 : n <= 4 ? 0.28 : n <= 6 ? 0.24 : n <= 9 ? 0.2 : 0.17;
  const bW = Math.max(10, Math.min(52, gW * barFrac));

  const cx = (i: number) => PL + gW * i + gW / 2;
  const yAmt = (v: number) => PT + CH - Math.max(0, v) / scaleMax * CH;

  // Polices : même logique que la largeur des barres — peu de périodes → barres larges → chiffres plus grands
  const barLabelSz = Math.round(Math.min(13.2, Math.max(7.2, 5.6 + bW * 0.18)) * 10) / 10;
  const xLabelSz   = Math.round(Math.min(12, Math.max(7.8, 7.4 + (MAX_PERIODS - n) * 0.38)) * 10) / 10;

  const gridFracs = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div>
      {/* Légende */}
      <div className="flex items-center gap-5 mb-2 text-xs text-gray-500 px-1">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm inline-block bg-emerald-500 opacity-85" />CA TTC
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm inline-block bg-orange-400 opacity-85" />Marge nette
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }}>
        {/* Grille + axe gauche */}
        {gridFracs.map((f, gi) => {
          const y = PT + CH * (1 - f);
          return (
            <g key={gi}>
              <line x1={PL} y1={y} x2={W - PR} y2={y}
                stroke={f === 0 ? '#d1d5db' : '#f3f4f6'}
                strokeWidth={f === 0 ? 1.5 : 1} />
              {f > 0 && (
                <text x={PL - 5} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">
                  {(scaleMax * f).toFixed(0)}€
                </text>
              )}
            </g>
          );
        })}

        {/* Barres */}
        {data.map((p, i) => {
          const caH  = Math.max(0, p.ca) / scaleMax * CH;
          const netV = Math.max(0, p.net);
          const netH = netV / scaleMax * CH;
          const neg  = p.net < 0;
          const cx_  = cx(i);
          const gap  = Math.max(1, bW * 0.12);
          const isHov = hovered === i;

          // Position étiquettes : au-dessus de chaque barre, mais bornée pour
          // rester dans la zone lisible (jamais trop proche de la ligne de base).
          const labelFloor = PT + CH - barLabelSz - 6; // max Y autorisé (au-dessus de la baseline)
          const caLblY  = Math.min(labelFloor, Math.max(PT + barLabelSz + 2, yAmt(p.ca)  - barLabelSz - 2));
          const netLblY = Math.min(labelFloor, Math.max(PT + barLabelSz + 2, yAmt(netV) - barLabelSz - 2));

          return (
            <g key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: 'default' }}>

              {isHov && (
                <rect x={PL + gW * i + 1} y={PT} width={gW - 2} height={CH}
                  fill="#f9fafb" rx={3} />
              )}

              {/* Barre CA */}
              <rect x={cx_ - bW - gap} y={yAmt(p.ca)} width={bW} height={caH}
                fill="#10b981" rx={2} opacity={isHov ? 1 : 0.82} />
              <text x={cx_ - bW / 2 - gap} y={caLblY}
                textAnchor="middle" fontSize={barLabelSz} fill="#059669" fontWeight="600">
                {p.ca.toFixed(0)}€
              </text>

              {/* Barre Marge */}
              <rect x={cx_ + gap} y={yAmt(netV)} width={bW} height={netH}
                fill={neg ? '#ef4444' : '#f97316'} rx={2} opacity={isHov ? 1 : 0.82} />
              <text x={cx_ + bW / 2 + gap} y={netLblY}
                textAnchor="middle" fontSize={barLabelSz}
                fill={neg ? '#dc2626' : '#ea580c'} fontWeight="600">
                {p.net.toFixed(0)}€
              </text>

              {/* Label X */}
              <text x={cx_} y={H - 6} textAnchor="middle" fontSize={xLabelSz}
                fill={isHov ? '#374151' : '#9ca3af'} fontWeight={isHov ? '600' : '400'}>
                {p.short_label}
              </text>

              {/* Tooltip */}
              {isHov && (() => {
                const pct = p.ca > 0 ? (p.net / p.ca) * 100 : 0;
                const tx  = Math.min(Math.max(cx_ - 64, PL), W - PR - 136);
                return (
                  <g>
                    <rect x={tx} y={PT + 2} width={134} height={68} rx={7}
                      fill="white" stroke="#e5e7eb" strokeWidth={1.2}
                      style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.09))' }} />
                    <text x={tx + 10} y={PT + 19} fontSize={10.5} fill="#111827" fontWeight="700">{p.label}</text>
                    <text x={tx + 10} y={PT + 35} fontSize={10} fill="#059669">CA TTC : {p.ca.toFixed(2)} €</text>
                    <text x={tx + 10} y={PT + 50} fontSize={10} fill={neg ? '#dc2626' : '#ea580c'}>
                      Marge : {p.net.toFixed(2)} € ({pct.toFixed(1)}%)
                    </text>
                    <text x={tx + 10} y={PT + 64} fontSize={9.5} fill="#6b7280">
                      {p.count} vente{p.count > 1 ? 's' : ''}
                    </text>
                  </g>
                );
              })()}
            </g>
          );
        })}
      </svg>

    </div>
  );
};

// ── KPI card ──────────────────────────────────────────────────────────────────

interface KpiProps { label: string; value: string; sub?: string; trend?: 'up' | 'down' | null; accent?: string; }

const KpiCard: React.FC<KpiProps> = ({ label, value, sub, trend, accent = 'text-gray-900' }) => (
  <div>
    <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-xl font-bold ${accent} leading-tight`}>{value}</p>
    {sub && (
      <p className={`text-xs mt-1 flex items-center gap-1 ${trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-red-500' : 'text-gray-400'}`}>
        {trend === 'up' && <TrendingUp size={11} />}
        {trend === 'down' && <TrendingDown size={11} />}
        {sub}
      </p>
    )}
  </div>
);

const OPEX_PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'recurring', label: 'Mensuelle récurrente' },
  { value: 'one_time', label: 'Ponctuelle (une période)' },
];

/** Liste déroulante thème violet (évite le rendu natif du navigateur + menu hors zone scroll). */
const ThemedSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, className = '', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 168) });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const onScroll = () => updatePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePos]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      const el = e.target as Node;
      if (btnRef.current?.contains(el)) return;
      if ((e.target as HTMLElement).closest?.('[data-themed-select-menu]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const selected = options.find(o => o.value === value)?.label ?? value;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        className={`text-xs border border-violet-200 rounded-lg px-2.5 py-1.5 bg-white text-left flex items-center justify-between gap-2 text-gray-800 shadow-sm hover:border-violet-400 hover:bg-violet-50/50 focus:outline-none focus:ring-2 focus:ring-violet-200 disabled:opacity-50 min-h-[2rem] ${className}`}
      >
        <span className="truncate">{selected}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-violet-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && menuPos && typeof document !== 'undefined' && createPortal(
        <ul
          data-themed-select-menu
          className="fixed z-[9999] rounded-xl border border-violet-200 bg-white py-1 shadow-xl max-h-60 overflow-y-auto"
          style={{ top: menuPos.top, left: menuPos.left, minWidth: menuPos.width }}
        >
          {options.map(o => (
            <li key={o.value}>
              <button
                type="button"
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${o.value === value ? 'bg-violet-100 text-violet-800 font-semibold' : 'text-gray-700 hover:bg-violet-50'}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </>
  );
};

// ── P&L Table ─────────────────────────────────────────────────────────────────

interface PlTableProps {
  periods: AccountingPeriod[];
  opexItems: OpexItem[];
  view: string;
  onAddOpex: (name: string, amount: number, periodType: string, periodKey?: string) => void;
  onUpdateOpex: (id: number, name: string, amount: number, periodType: string, periodKey?: string) => void;
  onDeleteOpex: (id: number) => void;
}

const PlTable: React.FC<PlTableProps> = ({ periods, opexItems, view, onAddOpex, onUpdateOpex, onDeleteOpex }) => {
  const [showAdd, setShowAdd]     = useState(false);
  const [addName, setAddName]     = useState('');
  const [addAmt,  setAddAmt]      = useState('');
  const [addType, setAddType]     = useState<'recurring' | 'one_time'>('recurring');
  const [addKey,  setAddKey]      = useState('');

  const [editId,   setEditId]   = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editAmt,  setEditAmt]  = useState('');
  const [editType, setEditType] = useState<'recurring' | 'one_time'>('recurring');
  const [editKey,  setEditKey]  = useState('');

  // Filtrer les périodes vides, limiter à 12
  const filtered = periods.filter(p => p.count > 0 || p.ca > 0);
  const cols = filtered.slice(-MAX_PERIODS);

  const factor = opexFactor(view);

  /** TVA et emballage : agrégés côté API (fiches ventes, défaut paramètres si non saisi) */
  const periodTva = (p: AccountingPeriod) => p.tva_amount ?? 0;
  const periodCaHt = (p: AccountingPeriod) => p.ca - periodTva(p);
  const periodPack = (p: AccountingPeriod) => p.packaging_total ?? 0;
  const periodMargeBrute = (p: AccountingPeriod) => periodCaHt(p) - p.purchases - periodPack(p);

  const totalCA    = cols.reduce((s, p) => s + p.ca, 0);
  const totalPurch = cols.reduce((s, p) => s + p.purchases, 0);
  const totalTva   = cols.reduce((s, p) => s + periodTva(p), 0);
  const totalCaHt  = totalCA - totalTva;
  const totalPack  = cols.reduce((s, p) => s + periodPack(p), 0);
  const totalMargeB = totalCaHt - totalPurch - totalPack;

  const totalOpex  = cols.reduce((s, p) => s + opexForPeriod(opexItems, p.key, factor), 0);
  const totalEbitda = totalMargeB - totalOpex;
  const totalPct    = totalCA > 0 ? (totalEbitda / totalCA) * 100 : 0;

  const COL_W   = 72;
  const LABEL_W = 162;

  function cellVal(n: number, bold = false): React.ReactNode {
    return <span className={`tabular-nums ${numCls(n, bold)}`}>{fmt(n)}</span>;
  }

  const handleAdd = () => {
    if (!addName.trim()) return;
    onAddOpex(addName.trim(), parseFloat(addAmt) || 0, addType, addType === 'one_time' ? addKey : undefined);
    setShowAdd(false); setAddName(''); setAddAmt(''); setAddType('recurring'); setAddKey('');
  };

  const handleEdit = () => {
    if (editId === null) return;
    onUpdateOpex(editId, editName, parseFloat(editAmt) || 0, editType, editType === 'one_time' ? editKey : undefined);
    setEditId(null);
  };

  if (!cols.length) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-sm text-gray-400">
        Aucune vente expédiée ou finalisée — le compte de résultat sera disponible dès la première vente.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <Euro size={16} className="text-gray-400" />
          Compte de résultat
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" style={{ minWidth: LABEL_W + COL_W * (cols.length + 1) }}>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left text-xs font-semibold text-gray-500 px-4 py-2.5 sticky left-0 bg-gray-50 z-10"
                style={{ width: LABEL_W, minWidth: LABEL_W }}>Libellé</th>
              {cols.map(p => (
                <th key={p.key} className="text-right text-xs font-semibold text-gray-500 px-2 py-2.5"
                  style={{ width: COL_W, minWidth: COL_W }}>{p.short_label}</th>
              ))}
              <th className="text-right text-xs font-bold text-gray-700 px-3 py-2.5 bg-gray-100 sticky right-0"
                style={{ width: COL_W + 4, minWidth: COL_W + 4 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {/* CA TTC */}
            <tr className="border-b border-gray-50 bg-emerald-50/30 hover:bg-emerald-50/60 transition-colors">
              <td className="px-4 py-2.5 font-semibold text-gray-800 sticky left-0 bg-emerald-50/30">CA TTC</td>
              {cols.map(p => <td key={p.key} className="text-right px-2 py-2.5">{cellVal(p.ca, true)}</td>)}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-emerald-50/60 font-bold">{cellVal(totalCA, true)}</td>
            </tr>

            {/* TVA : somme des montants par vente (affichée en négatif) */}
            <tr className="border-b border-gray-50 hover:bg-gray-50/40 transition-colors">
              <td className="px-4 py-2.5 text-gray-500 sticky left-0 bg-white pl-8">
                − TVA <span className="text-gray-400 text-xs font-normal">(montants fiches ventes)</span>
              </td>
              {cols.map(p => <td key={p.key} className="text-right px-2 py-2.5">{cellVal(-periodTva(p))}</td>)}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-gray-50">{cellVal(-totalTva)}</td>
            </tr>

            {/* CA HT */}
            <tr className="border-b border-gray-100 bg-slate-50/50 hover:bg-slate-50/80 transition-colors">
              <td className="px-4 py-2.5 font-medium text-gray-800 sticky left-0 bg-slate-50/50">CA HT</td>
              {cols.map(p => <td key={p.key} className="text-right px-2 py-2.5">{cellVal(periodCaHt(p), true)}</td>)}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-slate-100/80 font-semibold">{cellVal(totalCaHt, true)}</td>
            </tr>

            {/* Achats */}
            <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
              <td className="px-4 py-2.5 text-gray-500 sticky left-0 bg-white pl-8">− Achats</td>
              {cols.map(p => <td key={p.key} className="text-right px-2 py-2.5">{cellVal(-p.purchases)}</td>)}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-gray-50">{cellVal(-totalPurch)}</td>
            </tr>

            {/* Frais d'emballage : somme des montants sur les fiches (ventes comptabilisées) */}
            <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
              <td className="px-4 py-2.5 text-gray-500 sticky left-0 bg-white pl-8">
                − Frais d&apos;emballage
              </td>
              {cols.map(p => <td key={p.key} className="text-right px-2 py-2.5">{cellVal(-periodPack(p))}</td>)}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-gray-50">{cellVal(-totalPack)}</td>
            </tr>

            {/* Marge brute */}
            <tr className="border-b-2 border-gray-200 border-t border-gray-100 hover:bg-orange-50/30 transition-colors">
              <td className="px-4 py-2.5 font-bold text-gray-800 sticky left-0 bg-white">=&nbsp; Marge brute</td>
              {cols.map(p => <td key={p.key} className="text-right px-2 py-2.5">{cellVal(periodMargeBrute(p), true)}</td>)}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-gray-50 font-bold">{cellVal(totalMargeB, true)}</td>
            </tr>

            {/* ── Autres charges (ligne permanente) ── */}
            <tr className="border-b border-gray-100 hover:bg-red-50/20 transition-colors">
              <td className="px-4 py-2.5 text-gray-600 sticky left-0 bg-white pl-8">
                <div className="flex items-center gap-2">
                  <span>− Autres charges</span>
                  <button
                    onClick={() => { setShowAdd(s => !s); setAddName(''); setAddAmt(''); setAddType('recurring'); setAddKey(cols[0]?.key ?? ''); }}
                    title="Ajouter une charge"
                    className="flex items-center justify-center w-5 h-5 rounded-full bg-violet-100 hover:bg-violet-200 text-violet-600 transition-colors flex-shrink-0"
                  ><Plus size={11} /></button>
                </div>
              </td>
              {cols.map(p => (
                <td key={p.key} className="text-right px-2 py-2.5">
                  {cellVal(-opexForPeriod(opexItems, p.key, factor))}
                </td>
              ))}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-gray-50">{cellVal(-totalOpex)}</td>
            </tr>

            {/* Sous-lignes charges individuelles */}
            {opexItems.map(o => {
              const isEditing = editId === o.id;
              const isRecurring = !o.period_type || o.period_type === 'recurring';

              // Valeur par colonne
              const valForCol = (key: string) =>
                isRecurring ? -(o.monthly_amount * factor) : (o.period_key === key ? -o.monthly_amount : 0);
              const totalItem = cols.reduce((s, p) => s + Math.abs(valForCol(p.key)), 0);

              return (
                <tr key={o.id} className="border-b border-gray-50 hover:bg-red-50/10 transition-colors group">
                  <td className="px-4 py-1.5 sticky left-0 bg-white">
                    {isEditing ? (
                      <div className="flex items-center gap-1.5 pl-10 flex-wrap">
                        <input value={editName} onChange={e => setEditName(e.target.value)}
                          className="flex-1 min-w-[100px] text-xs border border-violet-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300" />
                        <input type="number" value={editAmt} onChange={e => setEditAmt(e.target.value)}
                          className="w-20 text-xs border border-violet-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300" />
                        <ThemedSelect
                          value={editType}
                          onChange={v => setEditType(v as 'recurring' | 'one_time')}
                          options={OPEX_PERIOD_OPTIONS}
                          className="min-w-[200px]"
                        />
                        {editType === 'one_time' && (
                          <ThemedSelect
                            value={editKey}
                            onChange={setEditKey}
                            options={cols.map(c => ({ value: c.key, label: c.label }))}
                            className="min-w-[140px]"
                          />
                        )}
                        <button onClick={handleEdit} className="text-violet-600 hover:text-violet-800 p-0.5"><Check size={13} /></button>
                        <button onClick={() => setEditId(null)} className="text-gray-400 hover:text-gray-600 p-0.5"><X size={13} /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 pl-10">
                        <span className="text-xs text-gray-400">↳ {o.name}</span>
                        <span className="text-gray-300 text-xs ml-1">
                          {isRecurring
                            ? `(${o.monthly_amount.toFixed(2)}€/mois)`
                            : `(ponctuel · ${cols.find(c => c.key === o.period_key)?.short_label ?? o.period_key})`}
                        </span>
                        <div className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => {
                              setEditId(o.id); setEditName(o.name); setEditAmt(String(o.monthly_amount));
                              setEditType(o.period_type === 'one_time' ? 'one_time' : 'recurring');
                              setEditKey(o.period_key ?? cols[0]?.key ?? '');
                            }}
                            className="p-1 rounded text-gray-400 hover:text-violet-600 hover:bg-violet-50"><Pencil size={11} /></button>
                          <button onClick={() => onDeleteOpex(o.id)}
                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"><Trash2 size={11} /></button>
                        </div>
                      </div>
                    )}
                  </td>
                  {cols.map(p => (
                    <td key={p.key} className="text-right px-2 py-1.5 text-xs text-gray-400 tabular-nums">
                      {valForCol(p.key) !== 0 ? `${fmt(valForCol(p.key))}` : <span className="text-gray-200">—</span>}
                    </td>
                  ))}
                  <td className="text-right px-3 py-1.5 sticky right-0 bg-gray-50 text-xs text-gray-400 tabular-nums">
                    −{totalItem.toFixed(2)} €
                  </td>
                </tr>
              );
            })}

            {/* Formulaire ajout inline */}
            {showAdd && (
              <tr className="border-b border-violet-100 bg-violet-50/30">
                <td className="px-4 py-2 sticky left-0 bg-violet-50/30">
                  <div className="flex items-center gap-2 pl-10 flex-wrap">
                    <input
                      autoFocus
                      value={addName}
                      onChange={e => setAddName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setShowAdd(false); }}
                      placeholder="Nom de la charge"
                      className="flex-1 min-w-[110px] text-xs border border-violet-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300 bg-white"
                    />
                    <input
                      type="number"
                      value={addAmt}
                      onChange={e => setAddAmt(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setShowAdd(false); }}
                      placeholder="Montant"
                      className="w-24 text-xs border border-violet-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300 bg-white"
                    />
                    <ThemedSelect
                      value={addType}
                      onChange={v => setAddType(v as 'recurring' | 'one_time')}
                      options={OPEX_PERIOD_OPTIONS}
                      className="min-w-[200px]"
                    />
                    {addType === 'one_time' && (
                      <ThemedSelect
                        value={addKey}
                        onChange={setAddKey}
                        options={cols.map(c => ({ value: c.key, label: c.label }))}
                        className="min-w-[140px]"
                      />
                    )}
                    {addType === 'recurring' && (
                      <span className="text-xs text-gray-400 whitespace-nowrap">€/mois</span>
                    )}
                    <button onClick={handleAdd}
                      className="p-1 rounded text-violet-600 hover:text-violet-800 hover:bg-violet-100"><Check size={13} /></button>
                    <button onClick={() => setShowAdd(false)}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"><X size={13} /></button>
                  </div>
                </td>
                {cols.map(p => <td key={p.key} className="bg-violet-50/20" />)}
                <td className="sticky right-0 bg-violet-50/30" />
              </tr>
            )}

            {/* EBITDA */}
            <tr className="border-b-2 border-gray-200 border-t border-gray-200 bg-orange-50/20 hover:bg-orange-50/40 transition-colors">
              <td className="px-4 py-2.5 font-bold text-gray-800 sticky left-0 bg-orange-50/20">= EBITDA</td>
              {cols.map(p => {
                const ebitda = periodMargeBrute(p) - opexForPeriod(opexItems, p.key, factor);
                return <td key={p.key} className="text-right px-2 py-2.5">{cellVal(ebitda, true)}</td>;
              })}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-orange-50/40 font-bold">{cellVal(totalEbitda, true)}</td>
            </tr>

            {/* % Marge EBITDA */}
            <tr className="hover:bg-violet-50/20 transition-colors">
              <td className="px-4 py-2.5 text-gray-500 text-xs sticky left-0 bg-white">% Marge EBITDA</td>
              {cols.map(p => {
                const ebitda = periodMargeBrute(p) - opexForPeriod(opexItems, p.key, factor);
                const pct = p.ca > 0 ? (ebitda / p.ca) * 100 : 0;
                return (
                  <td key={p.key} className="text-right px-2 py-2.5 text-xs">
                    <span className={`font-medium tabular-nums ${pct >= 0 ? 'text-violet-600' : 'text-red-500'}`}>
                      {fmtPct(pct)}
                    </span>
                  </td>
                );
              })}
              <td className="text-right px-3 py-2.5 sticky right-0 bg-gray-50 text-xs">
                <span className={`font-bold tabular-nums ${totalPct >= 0 ? 'text-violet-600' : 'text-red-500'}`}>
                  {fmtPct(totalPct)}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

interface Props { showNotification: ShowNotificationFn; crm_email_active?: boolean; is_free_plan?: boolean; onUpgrade?: () => void; }

const CrmComptabilite: React.FC<Props> = ({ showNotification, crm_email_active, is_free_plan, onUpgrade }) => {
  const [view, setView] = useState<'monthly' | 'annual' | 'weekly'>('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<AccountingData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // On envoie toujours l'année (même en vue annuelle) pour year_totals
      const res = await apiClient.get(`/crm/accounting?view=${view}&year=${year}`);
      setData(res.data as AccountingData);
    } catch {
      showNotification('Erreur', 'Impossible de charger les données comptables', 'error');
    } finally {
      setLoading(false);
    }
  }, [view, year, showNotification]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddOpex = async (name: string, amount: number, periodType: string, periodKey?: string) => {
    try {
      await apiClient.post('/crm/accounting/opex', { name, monthly_amount: amount, period_type: periodType, period_key: periodKey ?? null });
      loadData();
    } catch { showNotification('Erreur', 'Impossible d\'ajouter le poste', 'error'); }
  };

  const handleUpdateOpex = async (id: number, name: string, amount: number, periodType: string, periodKey?: string) => {
    try {
      await apiClient.patch(`/crm/accounting/opex/${id}`, { name, monthly_amount: amount, period_type: periodType, period_key: periodKey ?? null });
      loadData();
    } catch { showNotification('Erreur', 'Impossible de modifier le poste', 'error'); }
  };

  const handleDeleteOpex = async (id: number) => {
    try {
      await apiClient.delete(`/crm/accounting/opex/${id}`);
      loadData();
    } catch { showNotification('Erreur', 'Impossible de supprimer le poste', 'error'); }
  };

  const currentYear = new Date().getFullYear();

  // KPI : toujours basé sur year_totals (indépendant de la vue)
  const kpi = data?.year_totals;
  const marginPct = kpi && kpi.ca > 0 ? (kpi.net / kpi.ca) * 100 : 0;

  return (
    <div className="relative flex-1 overflow-y-auto bg-gray-50/50 min-h-0">
      {is_free_plan && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center border border-gray-100">
            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <Lock size={28} className="text-blue-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Section réservée</h3>
            <p className="text-sm text-gray-600 mb-6">La comptabilité n'est pas disponible dans le plan gratuit. Passez à Starter pour y accéder.</p>
            <button
              onClick={onUpgrade}
              className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-full font-semibold text-sm hover:from-teal-600 hover:to-cyan-600 transition-all shadow"
            >
              Voir les plans
            </button>
          </div>
        </div>
      )}

      {/* ── Header sticky ── */}
      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100/80 px-6 pt-5 pb-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart2 size={22} className="text-orange-500" />
              Comptabilité
            </h1>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto min-w-0 max-w-xl">
            {!crm_email_active && <CrmEmailSyncBanner />}
            <div className="flex justify-end">
              <button onClick={loadData} className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 shadow-sm">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Vue */}
          <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
            {(['monthly', 'annual', 'weekly'] as const).map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`text-sm px-4 py-1.5 rounded-lg font-medium transition-all ${view === v ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                {v === 'monthly' ? 'Mensuel' : v === 'annual' ? 'Annuel' : 'Hebdomadaire'}
              </button>
            ))}
          </div>

          {/* Sélecteur d'année — toujours visible, fixe les KPI */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl px-2 py-1 shadow-sm">
            <button onClick={() => setYear(y => y - 1)}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-400"><ChevronLeft size={16} /></button>
            <span className="text-sm font-semibold text-gray-700 min-w-[42px] text-center">{year}</span>
            <button onClick={() => setYear(y => Math.min(y + 1, currentYear))}
              disabled={year >= currentYear}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-30"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* ── Contenu ── */}
      <div className="px-6 pb-6 pt-5">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw size={28} className="animate-spin text-orange-400" />
          </div>
        ) : !data ? null : (
          <>
            <div className="flex gap-4 mb-5" style={{ alignItems: 'stretch' }}>

              {/* KPI panel — valeurs fixes à l'année sélectionnée */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col justify-between gap-4"
                style={{ width: '21%', minWidth: 168, flexShrink: 0 }}>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                    Exercice {year}
                  </p>
                  <div className="space-y-4">
                    <KpiCard
                      label="Chiffre d'affaires"
                      value={fmt(kpi?.ca ?? 0)}
                      sub={`${kpi?.count ?? 0} vente${(kpi?.count ?? 0) > 1 ? 's' : ''}`}
                      accent="text-emerald-700"
                    />
                    <div className="border-t border-gray-100" />
                    <KpiCard
                      label="Marge nette"
                      value={fmt(kpi?.net ?? 0)}
                      sub={`${marginPct.toFixed(1)} % du CA`}
                      trend={marginPct >= 0 ? 'up' : 'down'}
                      accent={(kpi?.net ?? 0) >= 0 ? 'text-orange-600' : 'text-red-600'}
                    />
                    <div className="border-t border-gray-100" />
                    <KpiCard
                      label="Valeur stock actuel"
                      value={fmt(data.stock_value ?? 0)}
                      sub="Coût total d'achat en stock"
                      accent="text-violet-700"
                    />
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Seules les ventes expédiées entrent dans ces calculs
                  </p>
                </div>
              </div>

              {/* Graphique */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex-1 min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-800">
                    {view === 'monthly' ? `Évolution mensuelle — ${year}` :
                     view === 'weekly'  ? `Évolution hebdomadaire — ${year}` :
                     'Évolution annuelle'}
                  </h3>
                </div>
                <AccountingChart periods={data.periods} view={view} />
              </div>
            </div>

            <PlTable
              periods={data.periods}
              opexItems={data.opex_items}
              view={view}
              onAddOpex={handleAddOpex}
              onUpdateOpex={handleUpdateOpex}
              onDeleteOpex={handleDeleteOpex}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CrmComptabilite;
